/**
 * Main layout component providing navigation and user interface structure.
 * Features responsive sidebar, top navigation, and user profile management.
 *
 * Key Features:
 * - Responsive sidebar with collapsible menu
 * - User profile display and authentication status
 * - Navigation menu with active route highlighting
 * - Support for both Auth0 and local authentication
 *
 * @see {@link components/ui/button.tsx} for UI components
 * @see {@link lib/utils.ts} for utility functions
 */

import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { motion } from "framer-motion";
import {
  // LayoutDashboard, // COMMENTED OUT FOR FACEBOOK REVIEW - Dashboard not needed
  Settings,
  MessageSquare,
  Shield,
  Menu,
  X,
  LogOut,
  User,
  // BarChart3, // COMMENTED OUT FOR FACEBOOK REVIEW - Insights not needed
  HelpCircle,
  Send,
  Search,
  Globe,
  Megaphone,
  Loader2,
  ChevronDown,
  ExternalLink,
  Inbox,
  LayoutGrid,
} from "lucide-react";
import { useReviewCount } from "../hooks/useReviewCount";
import { Button } from "./ui/button";
import { MoujibLogo } from "./ui/logo";
import { cn } from "../lib/utils";

import FacebookPageSelector from "./FacebookPageSelector";
import PageRouteGuard from "./PageRouteGuard";
import SubscriptionBanner from "./SubscriptionBanner";
import SubscriptionChip from "./SubscriptionChip";
import { useFacebookPage } from "../contexts/FacebookPageContext";
import { resetLanding, SELECTED_PAGE_STORAGE_KEY } from "../lib/landing";
import { usePostFilter } from "../contexts/PostFilterContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user: auth0User } = useAuth0();
  const { selectedPage } = useFacebookPage();
  const { state: filterState } = usePostFilter();
  const reviewCount = useReviewCount(selectedPage?.id ?? null);

  // Use Auth0 user as the primary source of user information
  const user = auth0User;

  const navigation: Array<{
    name: string;
    href: string;
    icon: any;
    badge?: number;
  }> = [
    //{ name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    { name: "Pages & Accounts", href: "/accounts", icon: LayoutGrid },
    ...(selectedPage
      ? [
          {
            name: "Moderation Rules",
            href: `/accounts/${selectedPage.id}/moderation-rules`,
            icon: Shield,
          },
          {
            name: "Comments",
            href: `/accounts/${selectedPage.id}/comments`,
            icon: MessageSquare,
          },
          {
            name: "Review Queue",
            href: `/accounts/${selectedPage.id}/review`,
            icon: Inbox,
            badge: reviewCount,
          },
         // {
         //   name: "Comment Insights",
         //   href: `/accounts/${selectedPage.id}/insights`,
         //   icon: BarChart3,
         // },
          // COMMENTED OUT FOR FACEBOOK REVIEW - Message autoreply hidden temporarily
          // Will be used later when submitting full feature set
          // {
          //   name: "Message Autoreply",
          //   href: `/accounts/${selectedPage.id}/autoreply`,
          //   icon: MessageSquare, // Will use appropriate icon when enabled
          // },
        ]
      : []),
    { name: "Settings", href: "/settings", icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      console.log("🚪 [LOGOUT] Initiating logout process");

      // Clear any local auth state (but Auth0 manages the main session)
      localStorage.removeItem("deepchat_auth_state");
      // Drop the cached page too, so the next person to sign in on this browser
      // doesn't see the previous user's page name in the top bar. The real
      // memory lives server-side and is restored on their next login.
      localStorage.removeItem(SELECTED_PAGE_STORAGE_KEY);
      resetLanding();

      // Always logout through Auth0 (it handles the session properly)
      const logoutUrl = `${window.location.origin}/login`;

      await logout({
        logoutParams: {
          returnTo: logoutUrl,
        },
      });

      console.log("✅ [LOGOUT] Auth0 logout initiated");
    } catch (error) {
      console.error("🔴 [LOGOUT] Error during logout:", error);
      // If Auth0 logout fails, manually navigate to login
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          sidebarOpen ? "block" : "hidden"
        )}
      >
        <div
          className="fixed inset-0 bg-black/20"
          onClick={() => setSidebarOpen(false)}
        />
        <motion.div
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          exit={{ x: -300 }}
          className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r shadow-lg"
        >
          <div className="flex items-center justify-between p-4 border-b">
            <MoujibLogo size="sm" variant="color" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                  {!!item.badge && item.badge > 0 && (
                    <span
                      className={cn(
                        "ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold",
                        isActive
                          ? "bg-white text-blue-700"
                          : "bg-red-500 text-white"
                      )}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Help & Feedback Links */}
          <div className="px-4 py-2 border-t space-y-1">
            <Link
              to="/help"
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === "/help"
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              )}
            >
              <HelpCircle className="h-4 w-4" />
              <span>Help & Support</span>
            </Link>
            <Link
              to="/feedback"
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === "/feedback"
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              )}
            >
              <Send className="h-4 w-4" />
              <span>Feedback</span>
            </Link>
          </div>

          {/* Mobile user info and logout */}
          <div className="p-4 border-t bg-white">
            <div className="flex items-center space-x-3 mb-4">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || "User"}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                {user?.provider && (
                  <p className="text-xs text-blue-600 capitalize">
                    via {user.provider}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow min-h-0 bg-white border-r shadow-sm">
          <div className="flex items-center p-4 border-b">
            <MoujibLogo size="md" variant="color" />
          </div>
          <nav className="shrink-0 p-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                  {!!item.badge && item.badge > 0 && (
                    <span
                      className={cn(
                        "ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold",
                        isActive
                          ? "bg-white text-blue-700"
                          : "bg-red-500 text-white"
                      )}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Post Filter (only active on moderation pages) */}
          {filterState.isActive && filterState.handlers ? (
            <div className="flex-1 flex flex-col min-h-0 border-t">
              <div className="px-4 pt-3 pb-2 shrink-0">
                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Filter by Post
                </div>
                <div className="relative mb-2">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search posts..."
                    className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 outline-none transition-all"
                    value={filterState.searchQuery}
                    onChange={(e) =>
                      filterState.handlers!.onSearchChange(e.target.value)
                    }
                  />
                </div>
                <button
                  onClick={() => filterState.handlers!.onSelectGlobal()}
                  className={cn(
                    "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors border",
                    filterState.selectedId === null
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "text-gray-600 hover:bg-gray-50 border-transparent"
                  )}
                >
                  <Globe className="w-3.5 h-3.5" />
                  All Posts
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
                {filterState.posts.length > 0 ? (
                  <>
                    {filterState.posts.map((post) => {
                      const isSelected = filterState.selectedId === post.id;
                      return (
                        <div
                          key={post.id}
                          className={cn(
                            "w-full flex items-stretch rounded-lg transition-colors",
                            isSelected
                              ? "bg-white border border-gray-200 shadow-sm"
                              : "border border-transparent hover:bg-gray-50"
                          )}
                        >
                          <button
                            onClick={() =>
                              filterState.handlers!.onSelectPost(post.id)
                            }
                            className="flex-1 min-w-0 flex items-start p-2 text-left"
                          >
                            <div
                              className={cn(
                                "w-7 h-7 rounded flex items-center justify-center mr-2 shrink-0 overflow-hidden",
                                isSelected
                                  ? "bg-blue-100 text-blue-600"
                                  : "bg-gray-100 text-gray-500"
                              )}
                            >
                              {post.imageUrl ? (
                                <img
                                  src={post.imageUrl}
                                  alt="Post"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = "none";
                                  }}
                                />
                              ) : (
                                <Megaphone className="w-3.5 h-3.5" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={cn(
                                  "text-xs line-clamp-2 leading-snug",
                                  isSelected
                                    ? "text-gray-900 font-semibold"
                                    : "text-gray-600 font-medium"
                                )}
                              >
                                {post.message?.substring(0, 80) || "Untitled Post"}
                              </p>
                              {post.createdTime && (
                                <span className="text-[10px] text-gray-400 mt-0.5 block">
                                  {new Date(post.createdTime).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </button>
                          {post.permalinkUrl && (
                            <a
                              href={post.permalinkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center px-2 text-gray-400 hover:text-blue-600 shrink-0"
                              title="Open post on Facebook"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      );
                    })}

                    {filterState.hasMore && (
                      <button
                        onClick={() => filterState.handlers!.onLoadMore()}
                        disabled={filterState.loadingMore}
                        className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {filterState.loadingMore ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3 w-3" />
                            Load more
                          </>
                        )}
                      </button>
                    )}
                  </>
                ) : filterState.loadingMore ? (
                  <div className="space-y-1 pt-1">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-2 animate-pulse"
                      >
                        <div className="w-7 h-7 rounded bg-gray-200 shrink-0" />
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="h-2.5 bg-gray-200 rounded w-4/5" />
                          <div className="h-2 bg-gray-100 rounded w-2/5" />
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-center gap-2 pt-2 text-[11px] text-gray-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading posts…
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-xs text-gray-400 italic">
                    No posts found
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          {/* Help & Feedback Links */}
          <div className="px-4 py-2 border-t space-y-1">
            <Link
              to="/help"
              className={cn(
                "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === "/help"
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              )}
            >
              <HelpCircle className="h-4 w-4" />
              <span>Help & Support</span>
            </Link>
            <Link
              to="/feedback"
              className={cn(
                "flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === "/feedback"
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              )}
            >
              <Send className="h-4 w-4" />
              <span>Feedback</span>
            </Link>
          </div>

          {/* Desktop user info and logout */}
          <div className="p-4 border-t">
            <div className="flex items-center space-x-3 mb-4">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || "User"}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                {user?.provider && (
                  <p className="text-xs text-blue-600 capitalize">
                    via {user.provider}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>

              {/* Facebook Page Selector */}
              <FacebookPageSelector />
            </div>

            <div className="flex items-center space-x-4">
              {/* Plan / trial state, inline in this bar rather than a strip of
                  its own. Details live on Settings > Billing. */}
              <SubscriptionChip />

              <div className="hidden sm:flex items-center space-x-3">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || "User"}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className="text-sm">
                  <span className="font-medium text-gray-900">
                    {user?.name || "User"}
                  </span>
                  {user?.provider && (
                    <span className="text-blue-600 ml-2 capitalize">
                      via {user.provider}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="hidden sm:flex"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Only renders when moderation has actually stopped — that one state
            needs a full explanation and a CTA. Everything milder is the chip in
            the bar above. Non-sticky, so the mobile filter's viewport-relative
            sticky offset below is unaffected. */}
        <SubscriptionBanner />

        {/* Mobile post filter (only on moderation pages, hidden on lg+ where the sidebar filter is visible) */}
        {filterState.isActive && filterState.handlers && (
          <div className="lg:hidden sticky top-[60px] z-30 bg-white border-b px-4 py-2 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-gray-500 shrink-0" />
            <Select
              value={filterState.selectedId ?? "__all__"}
              onValueChange={(value) => {
                if (value === "__all__") {
                  filterState.handlers!.onSelectGlobal();
                } else {
                  filterState.handlers!.onSelectPost(value);
                }
              }}
            >
              <SelectTrigger className="flex-1 h-9 text-sm">
                <SelectValue placeholder="Filter by post" />
              </SelectTrigger>
              <SelectContent className="max-h-[320px]">
                <SelectItem value="__all__">
                  <span className="flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5" /> All Posts
                  </span>
                </SelectItem>
                {filterState.posts.map((post) => (
                  <SelectItem key={post.id} value={post.id}>
                    <span className="truncate block max-w-[240px]">
                      {post.message?.substring(0, 60) || "Untitled Post"}
                    </span>
                  </SelectItem>
                ))}
                {filterState.hasMore && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      filterState.handlers!.onLoadMore();
                    }}
                    disabled={filterState.loadingMore}
                    className="w-full mt-1 flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 border-t border-gray-100 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {filterState.loadingMore ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" /> Load more
                      </>
                    )}
                  </button>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Page content */}
        <main className="p-4 sm:p-6">
          <PageRouteGuard>
            <Outlet />
          </PageRouteGuard>
        </main>
      </div>
    </div>
  );
};

export default Layout;
